import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Button, Tag, Input, Select, Modal, Popconfirm, Spin, Result, message, Progress, Divider,
} from 'antd';
import {
  ArrowLeftOutlined, CheckOutlined, UndoOutlined, DeleteOutlined, MessageOutlined,
  PaperClipOutlined, CloseCircleFilled, UserSwitchOutlined, RedoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getWorkTask, getAssignableUsers, toggleWorkTaskComplete, deleteWorkTask, appendWorkTaskNote,
  uploadWorkTaskAttachment, deleteWorkTaskAttachment, handoffWorkTask, reopenWorkTaskAssignee,
} from '../api/workTasks';
import useAuthStore from '../store/authStore';
import { urgencyMeta } from '../constants/agendaUrgency';
import WorkTaskTimeline from '../components/workTasks/WorkTaskTimeline';
import { linkifyText } from '../utils/linkify';
import { buildTimeline, ATTACHMENT_ACCEPT, workTaskDetailPath } from '../components/workTasks/workTaskHelpers';

const { TextArea } = Input;
const { Option } = Select;
const MANAGER_ROLES = ['branch_manager', 'super_admin', 'developer_admin'];

/**
 * Tek bir işin detay sayfası.
 *
 * Bildirimden ("X kişisi şu işe not ekledi") doğrudan buraya gelinir -
 * notifications.work_task_id bu sayfanın adresini üretiyor. Önceden yalnızca
 * liste sayfası vardı ve kullanıcı bildirimden sonra işi elle aramak zorunda
 * kalıyordu.
 */
const WorkTaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isManager = MANAGER_ROLES.includes(user?.role);

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeNote, setCompleteNote] = useState('');
  const [completeSubmitting, setCompleteSubmitting] = useState(false);

  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteFile, setNoteFile] = useState(null);
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const noteFileInputRef = useRef(null);

  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffAssignee, setHandoffAssignee] = useState(null);
  const [handoffOptions, setHandoffOptions] = useState([]);
  const [handoffLoading, setHandoffLoading] = useState(false);
  const [handoffNote, setHandoffNote] = useState('');
  const [handoffSubmitting, setHandoffSubmitting] = useState(false);
  const handoffSearchTimer = useRef(null);

  // Tekrar acma: coklu atamada birisi "tamamladim" dedigi halde is bitmemis
  // olabiliyor. Sebep zorunlu - kisiye bildirim gidiyor.
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenSubmitting, setReopenSubmitting] = useState(false);

  const [attachProgress, setAttachProgress] = useState(0);
  const [attaching, setAttaching] = useState(false);
  const attachInputRef = useRef(null);

  const [preview, setPreview] = useState(null);

  const fetchItem = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWorkTask(id);
      setItem(res.data);
      setError(null);
    } catch (e) {
      // 403 ve 404 farklı mesaj hak ediyor: biri "yetkin yok", diğeri
      // "silinmiş/yok". Ikisini tek mesaja indirmek kullaniciyi yanıltır.
      setError(e?.response?.status === 403
        ? 'Bu işi görüntüleme yetkiniz yok.'
        : 'İş bulunamadı. Silinmiş olabilir.');
      setItem(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  const assignees = item?.assignees || [];
  const myAssignee = assignees.find((a) => a.user?.id === user?.id);
  const isFullyCompleted = assignees.length > 0 && assignees.every((a) => a.is_completed);
  const canDelete  = item?.assigned_by?.id === user?.id || isManager;
  const canNote    = item?.assigned_by?.id === user?.id || !!myAssignee || isManager;
  const canHandoff = !!myAssignee && !myAssignee.is_completed;
  // Backend ile aynı kural: yalnızca atayan kişi veya yönetici.
  const canReopen  = item?.assigned_by?.id === user?.id || isManager;

  const handleToggleComplete = async (note) => {
    try {
      await toggleWorkTaskComplete(item.id, note);
      await fetchItem();
    } catch {
      message.error('İşlem başarısız.');
    }
  };

  const handleConfirmComplete = async () => {
    setCompleteSubmitting(true);
    try {
      await handleToggleComplete(completeNote.trim() || undefined);
      setCompleteOpen(false);
      setCompleteNote('');
    } finally {
      setCompleteSubmitting(false);
    }
  };

  const handleSubmitNote = async () => {
    if (!noteText.trim()) { message.warning('Not boş olamaz.'); return; }
    setNoteSubmitting(true);
    try {
      await appendWorkTaskNote(item.id, noteText.trim());
      // Dosya yüklemesi AYRI yakalanıyor: not zaten kaydedildi, dosya
      // yüklenemezse "Not eklenemedi" demek yanlış olurdu (kullanıcı notu
      // tekrar yazmaya kalkar ve mükerrer not oluşur).
      if (noteFile) {
        try {
          await uploadWorkTaskAttachment(item.id, noteFile);
        } catch (fileErr) {
          message.error(fileErr?.response?.data?.message || 'Not eklendi ama dosya yüklenemedi.');
        }
      }
      setNoteOpen(false);
      setNoteText('');
      setNoteFile(null);
      await fetchItem();
      message.success('Not eklendi.');
    } catch (e) {
      message.error(e?.response?.data?.message || 'Not eklenemedi.');
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleHandoffSearch = (text) => {
    clearTimeout(handoffSearchTimer.current);
    if (!text || text.trim().length < 2) { setHandoffOptions([]); return; }
    handoffSearchTimer.current = setTimeout(async () => {
      setHandoffLoading(true);
      try {
        const res = await getAssignableUsers({ search: text.trim() });
        setHandoffOptions((res.data || []).filter((u) => u.id !== user?.id));
      } catch {
        setHandoffOptions([]);
      } finally {
        setHandoffLoading(false);
      }
    }, 300);
  };

  const handleSubmitHandoff = async () => {
    if (!handoffAssignee) { message.warning('Devredilecek kişiyi seçin.'); return; }
    setHandoffSubmitting(true);
    try {
      await handoffWorkTask(item.id, handoffAssignee, handoffNote.trim() || undefined);
      setHandoffOpen(false);
      setHandoffAssignee(null);
      setHandoffNote('');
      message.success('İş devredildi.');
      // Devirden sonra kullanici artik bu isin uzerinde islem yapamiyor;
      // sayfada kalip butonlari yeniden hesaplamak icin tazeliyoruz.
      await fetchItem();
    } catch (e) {
      message.error(e?.response?.data?.message || 'İş devredilemedi.');
    } finally {
      setHandoffSubmitting(false);
    }
  };

  const handleSubmitReopen = async () => {
    if (reopenReason.trim().length < 3) {
      message.warning('Neden geri aldığınızı yazın.');
      return;
    }
    setReopenSubmitting(true);
    try {
      await reopenWorkTaskAssignee(item.id, reopenTarget.user.id, reopenReason.trim());
      setReopenTarget(null);
      setReopenReason('');
      message.success('Tamamlama geri alındı, kişiye bildirim gitti.');
      await fetchItem();
    } catch (e) {
      message.error(e?.response?.data?.message || 'İşlem başarısız.');
    } finally {
      setReopenSubmitting(false);
    }
  };

  const handleAttachChange = async (e) => {
    const file = e.target.files?.[0];
    if (attachInputRef.current) attachInputRef.current.value = '';
    if (!file) return;
    setAttaching(true);
    setAttachProgress(0);
    try {
      await uploadWorkTaskAttachment(item.id, file, (evt) => {
        if (evt.total) setAttachProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      message.success('Dosya eklendi.');
      await fetchItem();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Dosya yüklenemedi.');
    } finally {
      setAttaching(false);
      setAttachProgress(0);
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    try {
      await deleteWorkTaskAttachment(item.id, attachment.id);
      message.success('Dosya silindi.');
      await fetchItem();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Dosya silinemedi.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteWorkTask(item.id);
      message.success('İş silindi.');
      navigate(workTaskDetailPath(user?.role));
    } catch (e) {
      message.error(e?.response?.data?.message || 'İş silinemedi.');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Spin size="large" /></div>;
  }

  if (error) {
    return (
      <Result
        status="warning"
        title={error}
        extra={<Button type="primary" onClick={() => navigate(workTaskDetailPath(user?.role))}>İş Takip'e Dön</Button>}
      />
    );
  }

  const meta = urgencyMeta(item.urgency);
  const timeline = buildTimeline(item);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(workTaskDetailPath(user?.role))}>Geri</Button>
        <h2 className="text-2xl font-bold m-0">İş Detayı</h2>
      </div>

      <Card className="mb-4">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Tag color={meta.color}>{meta.label}</Tag>
          {isFullyCompleted && <Tag color="green">Tamamlandı</Tag>}
          <span className="text-gray-400 text-xs">
            Oluşturma: {dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}
          </span>
        </div>

        <div
          style={{
            whiteSpace: 'pre-wrap',
            fontSize: 15,
            textDecoration: isFullyCompleted ? 'line-through' : 'none',
            color: isFullyCompleted ? '#999' : 'inherit',
          }}
        >
          {linkifyText(item.description)}
        </div>

        <Divider style={{ margin: '16px 0 12px' }} />

        <div className="text-sm">
          <div className="mb-1">
            <span className="text-gray-500">Atayan: </span>
            {item.assigned_by?.name} {item.assigned_by?.surname}
          </div>
          <div>
            <span className="text-gray-500">Atananlar: </span>
            {assignees.map((a, i) => (
              <span key={a.id}>
                {i > 0 && ', '}
                <span style={{ color: a.is_completed ? '#52c41a' : 'inherit' }}>
                  {a.is_completed && '✓ '}{a.user?.name} {a.user?.surname}
                </span>
                {/* Tekrar açma kişi bazında: çoklu atamada birinin
                    tamamlaması geri alınırken diğerleri etkilenmemeli. */}
                {a.is_completed && canReopen && (
                  <Button
                    type="link"
                    size="small"
                    icon={<RedoOutlined />}
                    style={{ padding: '0 4px', height: 'auto' }}
                    onClick={() => { setReopenTarget(a); setReopenReason(''); }}
                  >
                    Tekrar Aç
                  </Button>
                )}
              </span>
            ))}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap mt-4">
          {myAssignee && (
            myAssignee.is_completed ? (
              <Popconfirm
                title="Tamamlandı işaretini geri almak istiyor musunuz?"
                onConfirm={() => handleToggleComplete()}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button icon={<UndoOutlined />}>Geri Al</Button>
              </Popconfirm>
            ) : (
              <Button type="primary" icon={<CheckOutlined />} onClick={() => setCompleteOpen(true)}>
                Tamamlandı
              </Button>
            )
          )}
          {canHandoff && (
            <Button icon={<UserSwitchOutlined />} onClick={() => { setHandoffOpen(true); setHandoffOptions([]); }}>
              Devret
            </Button>
          )}
          {canNote && (
            <Button icon={<MessageOutlined />} onClick={() => setNoteOpen(true)}>Not Ekle</Button>
          )}
          {canNote && (
            <Button icon={<PaperClipOutlined />} loading={attaching} onClick={() => attachInputRef.current?.click()}>
              Dosya Ekle
            </Button>
          )}
          {canDelete && (
            <Popconfirm title="Bu işi silmek istediğinize emin misiniz?" onConfirm={handleDelete} okText="Evet" cancelText="Hayır">
              <Button danger icon={<DeleteOutlined />}>Sil</Button>
            </Popconfirm>
          )}
        </div>
      </Card>

      <Card title={`İşlem Geçmişi (${timeline.length})`}>
        <WorkTaskTimeline
          timeline={timeline}
          currentUser={user}
          isManager={isManager}
          onPreview={setPreview}
          onDeleteAttachment={handleDeleteAttachment}
        />
      </Card>

      <input
        ref={attachInputRef}
        type="file"
        accept={ATTACHMENT_ACCEPT}
        style={{ display: 'none' }}
        onChange={handleAttachChange}
      />
      {attaching && attachProgress > 0 && (
        <Progress
          percent={attachProgress}
          size="small"
          style={{ position: 'fixed', bottom: 16, right: 16, width: 240, background: '#fff', padding: 8, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 2000 }}
        />
      )}

      <Modal
        title="İşi Tamamla"
        open={completeOpen}
        onCancel={() => setCompleteOpen(false)}
        onOk={handleConfirmComplete}
        confirmLoading={completeSubmitting}
        okText="Tamamla"
        cancelText="Vazgeç"
      >
        <p className="text-gray-500 mb-2">
          İsterseniz nasıl tamamlandığına dair bir açıklama yazın (opsiyonel).
        </p>
        <TextArea
          value={completeNote}
          onChange={(e) => setCompleteNote(e.target.value)}
          placeholder="Açıklama (opsiyonel)..."
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={2000}
        />
      </Modal>

      <Modal
        title="Not Ekle"
        open={noteOpen}
        onCancel={() => setNoteOpen(false)}
        onOk={handleSubmitNote}
        confirmLoading={noteSubmitting}
        okText="Ekle"
        cancelText="Vazgeç"
      >
        <TextArea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Gelişmeyi yazın..."
          autoSize={{ minRows: 3, maxRows: 8 }}
          maxLength={2000}
          autoFocus
        />
        <div className="mt-2">
          <input
            ref={noteFileInputRef}
            type="file"
            accept={ATTACHMENT_ACCEPT}
            style={{ display: 'none' }}
            onChange={(e) => setNoteFile(e.target.files?.[0] || null)}
          />
          {noteFile ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <PaperClipOutlined />
              <span>{noteFile.name}</span>
              <CloseCircleFilled className="text-gray-400 hover:text-red-500 cursor-pointer" onClick={() => setNoteFile(null)} />
            </div>
          ) : (
            <Button size="small" icon={<PaperClipOutlined />} onClick={() => noteFileInputRef.current?.click()}>
              Dosya Ekle (opsiyonel)
            </Button>
          )}
        </div>
      </Modal>

      <Modal
        title="Tamamlamayı Geri Al"
        open={!!reopenTarget}
        onCancel={() => setReopenTarget(null)}
        onOk={handleSubmitReopen}
        confirmLoading={reopenSubmitting}
        okText="Tekrar Aç"
        cancelText="Vazgeç"
      >
        <p className="text-gray-500 mb-2">
          <b>{reopenTarget?.user?.name} {reopenTarget?.user?.surname}</b> kişisinin
          tamamlaması geri alınacak ve kendisine bildirim gidecek. Sebep işlem
          geçmişine de not olarak yazılır.
        </p>
        <TextArea
          value={reopenReason}
          onChange={(e) => setReopenReason(e.target.value)}
          placeholder="Neden geri alıyorsunuz? (zorunlu)"
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={500}
          autoFocus
        />
      </Modal>

      <Modal
        title="İşi Devret"
        open={handoffOpen}
        onCancel={() => setHandoffOpen(false)}
        onOk={handleSubmitHandoff}
        confirmLoading={handoffSubmitting}
        okText="Devret"
        cancelText="Vazgeç"
      >
        <p className="text-gray-500 mb-2">
          Bu iş devredilecek kişinin listesine geçecek, siz artık bu iş üzerinde işlem yapamayacaksınız.
        </p>
        <Select
          showSearch
          value={handoffAssignee}
          onChange={setHandoffAssignee}
          onSearch={handleHandoffSearch}
          filterOption={false}
          loading={handoffLoading}
          placeholder="Kime devredilecek? (en az 2 karakter yazın)"
          style={{ width: '100%' }}
          notFoundContent={handoffLoading ? 'Aranıyor...' : 'Kullanıcı bulunamadı'}
        >
          {handoffOptions.map((u) => (
            <Option key={u.id} value={u.id}>
              {u.name} {u.surname}{u.branch?.name ? ` (${u.branch.name})` : ''}
            </Option>
          ))}
        </Select>
        <TextArea
          className="mt-3"
          value={handoffNote}
          onChange={(e) => setHandoffNote(e.target.value)}
          placeholder="Devir notu (opsiyonel) — ör. şu ana kadar yapılanlar..."
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={2000}
        />
      </Modal>

      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        width={800}
        destroyOnHidden
        title={preview?.name}
      >
        {preview?.isImage ? (
          <img src={preview.url} alt="" style={{ width: '100%', borderRadius: 8 }} />
        ) : preview && (
          <iframe src={preview.url} title={preview.name} style={{ width: '100%', height: '75vh', border: 'none' }} />
        )}
      </Modal>
    </div>
  );
};

export default WorkTaskDetail;
